/*
 * The button that writes the replay file (chunk U-0's split of `panels.ts`).
 *
 * The other of the two. It is also one of the three places in this whole HUD
 * that takes a click, and it says so for itself — and it stops the click
 * reaching the canvas underneath, because a player who pressed *save* did not
 * ask to be put back into the game.
 */

/**
 * The "save your session" button.
 *
 * Phase 4 decision 7's whole session-capture story: a playtester presses this,
 * a replay file lands in their downloads, and `sim-cli verify` re-runs it
 * bit-for-bit against a fresh simulation. Nothing phones home and there is no
 * telemetry in this tree — the cut list is explicit — so a file somebody chooses
 * to send back is the only recording that exists.
 *
 * Two things about it are deliberate and both are about input:
 *
 * - **It turns pointer events back on for itself.** `.hud` sets
 *   `pointer-events: none` so the canvas underneath can take the click that
 *   acquires the pointer lock; anything here that wants to be clicked says so,
 *   which is the rule at the top of this file.
 * - **It is unreachable while the pointer is locked**, and that is fine, because
 *   `F9` is the same request from the keyboard. It is the path for a player who
 *   has already pressed escape — which is to say, for a player who has finished.
 */
export class SaveButton {
  private readonly element: HTMLButtonElement;

  constructor(
    root: HTMLElement,
    label: string,
    hint: string,
    onSave: () => void,
    variant: "save" | "skip" = "save",
  ) {
    this.element = document.createElement("button");
    this.element.type = "button";
    // The kit's `.btn`, composed alongside the client's own name (plan D2) —
    // and the graduation is a homecoming rather than a restyle: the lab drew
    // this button with this plate, this hairline and this ink, and U-0 lifted
    // the three of them into `.btn` (`docs/decisions/ui.md` §1.3). What is left
    // in `ui/hud.css` is the corner it stands in.
    //
    // **Not `.btn--bare`**, which the chunk's brief named: bare is transparent
    // with no edge, and it is the kit's *quiet textual choice inside a panel*.
    // This one stands on the world with nothing behind it and is the only thing
    // in the frame a mouse can press — "findable and ignorable in that order" is
    // this file's own rule and bare loses the first half of it.
    this.element.className =
      variant === "save" ? "hud__save btn" : "hud__save hud__save--skip btn";
    this.element.textContent = label;
    this.element.title = hint;
    this.element.addEventListener("click", (event) => {
      // The click must not reach the canvas: down there it is the gesture that
      // asks for the pointer lock, and a player who pressed *save* did not ask
      // to be put back into the game.
      event.stopPropagation();
      onSave();
    });
    root.append(this.element);
  }

  /** Replaces the tooltip, for a session where the button cannot do anything useful. */
  setHint(hint: string): void {
    this.element.title = hint;
  }
}
